import { useStableCallback } from '@main/shared/browser';
import { encodeColumnFilters } from '@main/shared/url-helpers';
import { isNonNullable } from '@main/shared/utils';
import { useNavigate } from '@tanstack/react-router';
import { ColumnDef, ColumnFiltersState, Updater } from '@tanstack/react-table';
import equal from 'fast-deep-equal';
import * as jsurl from 'jsurl2';
import { useEffect, useMemo, useState } from 'react';

import { isColumnFilter, isValidColumnFilter } from './filters';
import {
  getLegacyColumnIdMap,
  resolveLegacyColumnIdEntries,
  resolveLegacyColumnIdUpdater,
} from './legacy-column-ids';

/**
 * React hook which syncs table's column filter state to URL query search.
 * `searchParam` configures the search param name under which the state
 * will be persited in the URL.
 */
export function useTableFiltersQuery<TData>({
  columns,
  searchParam,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (ColumnDef<TData, any> | undefined)[];
  searchParam: string;
}) {
  const navigate = useNavigate();

  const [columnFilters, setRawColumnFilters] = useState(() =>
    decodeColumnFilters(new URLSearchParams(window.location.search).get(searchParam) ?? ''),
  );

  const legacyColumnIdMap = useMemo(() => getLegacyColumnIdMap(columns), [columns]);
  const setColumnFilters = useStableCallback((updater: Updater<ColumnFiltersState>) =>
    setRawColumnFilters(resolveLegacyColumnIdUpdater(updater, legacyColumnIdMap)),
  );

  useEffect(() => {
    void navigate({
      to: '.',
      search: (old: Record<string, unknown>) => ({
        ...old,
        [searchParam]: encodeColumnFilters(columnFilters),
      }),
      replace: true,
    });
  }, [navigate, searchParam, columnFilters]);

  const filteredColumnFilters = useMemo(
    () => getApplicableColumnFilters(columns, columnFilters),
    [columns, columnFilters],
  );
  const [filteredColumnFiltersState, setFilteredColumnFiltersState] =
    useState(filteredColumnFilters);
  useEffect(() => {
    if (!equal(filteredColumnFilters, filteredColumnFiltersState)) {
      return setFilteredColumnFiltersState(filteredColumnFilters);
    }
  }, [filteredColumnFilters, filteredColumnFiltersState]);

  return [filteredColumnFiltersState, setColumnFilters] as const;
}

/**
 * Moves filters saved under a column's legacy ID to the column's ID, then keeps
 * only the filters that the current columns can apply.
 */
export function getApplicableColumnFilters<TData>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (ColumnDef<TData, any> | undefined)[],
  filters: ColumnFiltersState,
): ColumnFiltersState {
  const definedColumns = columns.filter(isNonNullable);
  return resolveLegacyColumnIdEntries(filters, getLegacyColumnIdMap(definedColumns)).filter(
    (filter) => isValidColumnFilter(definedColumns, filter),
  );
}

function decodeColumnFilters(encodedFilters: string): ColumnFiltersState {
  try {
    const json = jsurl.parse(encodedFilters);
    if (!Array.isArray(json)) {
      return [];
    }
    return json.filter(isColumnFilter);
  } catch {
    return [];
  }
}
